import type {
  GameTypePlugin,
  GameRenderer,
  GameConfigurator,
  GameRendererCapabilities,
} from '../../interfaces/game-type-plugin.interface';
import { SportDefinitionRegistry } from './sport-definitions/sport-definition.registry';
import { GolfDrivingSportDefinition } from './sport-definitions/golf-driving/golf-driving.sport';
import { SportsRenderer } from './sports-renderer';
import { SportsGameConfigurator } from './sports-configurator';
import { FootballSportDefinition } from './sport-definitions/football/football.sport';
import { BasketballSportDefinition } from './sport-definitions/basketball/basketball.sport';
import { BaseballSportDefinition } from './sport-definitions/baseball/baseball.sport';
import { SoccerSportDefinition } from './sport-definitions/soccer/soccer.sport';
import { IceHockeySportDefinition } from './sport-definitions/ice-hockey/ice-hockey.sport';

/**
 * Sports game type plugin.
 *
 * Registers all sport definitions with SportDefinitionRegistry and exposes
 * factory methods for the renderer and configurator.
 *
 * Phase 1: Golf Driving only. Subsequent sports are added here when implemented.
 * Each new sport = one new import + one new SportDefinitionRegistry.register() call.
 */
export class SportsPlugin implements GameTypePlugin {
  readonly id = 'sports';
  readonly name = 'Sports Challenge';
  readonly description =
    'Skill-based sports mini-games: golf driving, field goals, free throws, and more.';
  readonly version = '0.1.0';
  readonly isBeta = true;

  readonly capabilities: GameRendererCapabilities = {
    mode: '3d-only',
    requires3DScene: true,
    requiresHTMLContainer: false,
    supportsAR: true,
    supportsVR: false,
  };

  constructor() {
    this.registerSportDefinitions();
  }

  createRenderer(): GameRenderer {
    return new SportsRenderer();
  }

  createConfigurator(): GameConfigurator {
    return new SportsGameConfigurator();
  }

  // ─── Private ─────────────────────────────────────────────────────────────────

  private registerSportDefinitions(): void {
    // Guard against double-registration during HMR
    if (!SportDefinitionRegistry.has('golf-driving')) {
      SportDefinitionRegistry.register(new GolfDrivingSportDefinition());
    }

    if (!SportDefinitionRegistry.has('football')) {
      SportDefinitionRegistry.register(new FootballSportDefinition());
    }
    if (!SportDefinitionRegistry.has('basketball')) {
      SportDefinitionRegistry.register(new BasketballSportDefinition());
    }
    if (!SportDefinitionRegistry.has('baseball')) {
      SportDefinitionRegistry.register(new BaseballSportDefinition());
    }
    if (!SportDefinitionRegistry.has('soccer')) {
      SportDefinitionRegistry.register(new SoccerSportDefinition());
    }
    if (!SportDefinitionRegistry.has('ice-hockey')) {
      SportDefinitionRegistry.register(new IceHockeySportDefinition());
    }
  }
}
